import { motion } from "framer-motion";
import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import clsx from "clsx";

import { useAppDispatch, useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { View, Text, Button, DropdownField, Card } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { agentFunctionActions } from "../../redux/reducers/agentFunctionSlice";
import { EmptyState } from "../../components/base/EmptyState";
import { DeploymentDetailsDialog } from "../../components/deployments/DeploymentDetailsDialog";
import { AgentDeployment, agentDeploymentActions } from "../../redux/reducers/agentDeploymentSlice";
import { capitalize } from "../../utils/strings";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3 },
  },
};

export const AgentDeploymentsPage = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { agentId } = useParams<{ agentId: string }>();
  const { organizationId } = useEffectiveOrganization();
  const agentDeploymentEntities = useAppSelector((state) => state.agentDeployment.entities);
  const agentEnvironmentEntities = useAppSelector((state) => state.agentEnvironment.entities);
  const agentFunctionEntities = useAppSelector((state) => state.agentFunction.entities);
  const isLoading = useAppSelector((state) => state.agentDeployment.isLoading);
  const metadata = useAppSelector((state) => state.agentDeployment.metadata);
  const selectedDeploymentId = searchParams.get("d") || "";
  const selectedEnvironmentId = searchParams.get("e") || "";

  // Get deployments from Redux store (server-side filtered and paginated)
  const agentDeployments = useMemo(() => {
    return Object.values(agentDeploymentEntities)
      .filter((deployment) => {
        return deployment.agentId === agentId && (selectedEnvironmentId ? deployment.environmentId === selectedEnvironmentId : true);
      })
      .sort((a, b) => new Date(b.deployedAt).getTime() - new Date(a.deployedAt).getTime());
  }, [agentDeploymentEntities, agentId, selectedEnvironmentId]);

  // Get environments for this agent (for filtering)
  const agentEnvironments = useMemo(() => {
    return Object.values(agentEnvironmentEntities).filter((environment) => environment.agentId === agentId);
  }, [agentEnvironmentEntities, agentId]);

  // Get active deployments for each environment using dedicated activeDeployment store
  const activeDeployments = useMemo(() => {
    return agentEnvironments.map((environment) => {
      const activeDeployment = Object.values(agentDeploymentEntities).find((deployment) => {
        return deployment.agentId === agentId && deployment.environmentId === environment.id && deployment.isActive;
      });

      return {
        environment,
        activeDeployment,
      };
    });
  }, [agentId, agentEnvironments, agentDeploymentEntities]);

  useEffect(() => {
    if (agentId && organizationId) {
      // Fetch agent functions
      dispatch(agentFunctionActions.get({ agentId }));

      // Fetch deployment history with server-side pagination
      dispatch(
        agentDeploymentActions.get({
          agentId,
          page: "1",
          limit: "20",
        }),
      );
    }
  }, [agentId, organizationId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "deployed":
        return "text-blue-700 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20";
      case "pending":
        return "text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20";
      case "failed":
        return "text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20";
      default:
        return "text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20";
    }
  };

  const getDedicatedPillColor = (isDedicated: boolean) => {
    if (isDedicated) {
      return "bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300";
    }

    return "bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400";
  };

  const getEnvironmentName = (environmentId: string) => {
    const environment = agentEnvironmentEntities[environmentId];
    return environment?.name || environmentId;
  };

  const getFunctionName = (environmentId: string, deployment?: AgentDeployment) => {
    // Use historical functionId from deployment snapshot if available
    const functionId = deployment?.functionId || agentEnvironmentEntities[environmentId]?.functionId;
    if (!functionId) return "No function";
    const agentFunction = agentFunctionEntities[functionId];
    return agentFunction?.displayName || agentFunction?.name || "Unknown function";
  };

  const getFunctionInfo = (environmentId: string, deployment?: AgentDeployment): { name: string; version?: string } | null => {
    // Use historical functionId from deployment snapshot if available
    const functionId = deployment?.functionId || agentEnvironmentEntities[environmentId]?.functionId;
    if (!functionId) return null;
    const agentFunction = agentFunctionEntities[functionId];
    if (!agentFunction) return null;

    return {
      name: agentFunction.displayName || agentFunction.name,
      version: agentFunction.updatedAt ? new Date(agentFunction.updatedAt).toLocaleString() : undefined,
    };
  };

  // Calculate pagination from server response
  const totalPages = Math.ceil((metadata?.total || 0) / (metadata?.limit || 20));
  const currentPage = metadata?.page || 1;

  const onClickViewDetails = (deployment: AgentDeployment) => {
    setSearchParams({ ...searchParams, d: deployment.id });
  };

  const onClickCloseDetails = () => {
    setSearchParams({ ...searchParams });
  };

  const handlePageChange = (page: number) => {
    if (agentId && organizationId) {
      dispatch(
        agentDeploymentActions.get({
          agentId,
          page: page.toString(),
          limit: "20",
          environment: selectedEnvironmentId,
        }),
      );
    }
  };

  if (isLoading && agentDeployments.length === 0) {
    return (
      <View className="flex flex-col items-center justify-center py-12">
        <View className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></View>
        <Text className="mt-4 text-gray-500">Loading deployments...</Text>
      </View>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6 p-4"
      >
        {/* Active Deployments Cards */}
        {agentEnvironments.length > 0 && (
          <View className="flex flex-col sm:flex-row gap-4">
            {activeDeployments.map(({ environment, activeDeployment }) => (
              <Card
                key={environment.id}
                className={clsx("p-4 w-full sm:w-[280px]", activeDeployment && "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors")}
                onClick={activeDeployment ? () => onClickViewDetails(activeDeployment) : undefined}
              >
                <View className="flex items-center justify-between mb-2">
                  <Text className="text-xs uppercase tracking-wider text-gray-500 mr-2">{environment.name}</Text>
                  {activeDeployment && (
                    <View className="flex flex-row items-center gap-2">
                      <View className={clsx("px-2 py-1 rounded-full text-xs font-medium", getDedicatedPillColor(activeDeployment.isDedicated))}>
                        {activeDeployment.isDedicated ? "Dedicated" : "Shared"}
                      </View>
                      <View className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-full text-xs font-medium">
                        Active
                      </View>
                    </View>
                  )}
                </View>
                {activeDeployment ? (
                  <View className="space-y-1">
                    <Text className="text-sm font-medium text-gray-900 dark:text-white block">
                      {getFunctionName(activeDeployment.environmentId, activeDeployment)}
                    </Text>
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">STT: {activeDeployment.sttConfig.sttModel}</Text>
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">
                      Deployed At: {new Date(activeDeployment.deployedAt).toLocaleString()}
                    </Text>
                  </View>
                ) : (
                  <View className="space-y-1">
                    <Text className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 block">No active deployment</Text>
                    <Text className="text-xs text-gray-500 dark:text-gray-400 block">Deploy to this environment to get started</Text>
                  </View>
                )}
              </Card>
            ))}
          </View>
        )}

        {/* Environment Filter */}
        {agentEnvironments.length > 1 && (
          <View className="flex justify-end">
            <View className="w-auto">
              <DropdownField
                name="environment"
                placeholder="All environments"
                value={selectedEnvironmentId}
                onChange={(value) => {
                  if (value) {
                    setSearchParams({ ...searchParams, e: value });
                  } else {
                    setSearchParams({ ...searchParams });
                  }

                  // Fetch filtered deployments from server
                  if (agentId) {
                    dispatch(
                      agentDeploymentActions.get({
                        agentId,
                        page: "1",
                        limit: "20",
                        environment: value || undefined,
                      }),
                    );
                  }
                }}
                options={[
                  { label: "All environments", value: "" },
                  ...agentEnvironments.map((env) => ({
                    label: env.name,
                    value: env.id,
                  })),
                ]}
              />
            </View>
          </View>
        )}

        {/* Deployments List */}
        {agentDeployments.length === 0 ? (
          <EmptyState
            icon="arrow-up"
            title="No deployments yet"
            description="Deployments will appear here when you deploy your agent to an environment."
          />
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}
            className="border border-gray-200 dark:border-gray-700 rounded-md"
          >
            {agentDeployments.map((deployment, index) => (
              <View
                key={deployment.id}
                onClick={() => onClickViewDetails(deployment)}
                className={`py-3 px-4 cursor-pointer transition-colors ${"bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700"} ${index !== 0 ? "border-t border-gray-200 dark:border-gray-700" : ""}`}
              >
                <View className="flex flex-row justify-between items-start">
                  <View className="flex flex-col flex-1 min-w-0">
                    <View className="flex flex-row items-center gap-2 mb-2 flex-wrap">
                      <Text className="font-medium text-sm">{getFunctionName(deployment.environmentId, deployment)}</Text>
                      <View className={clsx("px-2 py-1 rounded-full text-xs font-medium", getStatusColor(deployment.status))}>
                        {capitalize(deployment.status)}
                      </View>
                      <View className={clsx("px-2 py-1 rounded-full text-xs font-medium", getDedicatedPillColor(deployment.isDedicated))}>
                        {deployment.isDedicated ? "Dedicated" : "Shared"}
                      </View>
                      {deployment.isActive && (
                        <View className="px-2 py-1 bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 rounded-full text-xs font-medium">
                          Active
                        </View>
                      )}
                    </View>
                    {/* Mobile layout - stacked */}
                    <View className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                      <Text>
                        <span className="font-medium">Environment:</span>{" "}
                        <span className="text-gray-900 dark:text-white">{getEnvironmentName(deployment.environmentId)}</span>
                      </Text>
                      <Text>
                        <span className="font-medium">Type:</span>{" "}
                        <span className="text-gray-900 dark:text-white">
                          {deployment.deploymentType?.map((type) => type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()).join(", ") || "Unknown"}
                        </span>
                      </Text>
                      <Text>
                        <span className="font-medium">Deployed:</span>{" "}
                        <span className="text-gray-900 dark:text-white">{new Date(deployment.deployedAt).toLocaleString()}</span>
                      </Text>
                      <Text>
                        <span className="font-medium">By:</span> <span className="text-gray-900 dark:text-white">{deployment.deployedBy}</span>
                      </Text>
                    </View>
                    {/* Desktop layout - horizontal */}
                    <View className="hidden sm:flex flex-row items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <Text>
                        <span className="font-medium">Environment:</span>{" "}
                        <span className="text-gray-900 dark:text-white">{getEnvironmentName(deployment.environmentId)}</span>
                      </Text>
                      <Text>
                        <span className="font-medium">Deployed At:</span>{" "}
                        <span className="text-gray-900 dark:text-white">{new Date(deployment.deployedAt).toLocaleString()}</span>
                      </Text>
                      <Text>
                        <span className="font-medium">Deployment Type:</span>{" "}
                        <span className="text-gray-900 dark:text-white">
                          {deployment.deploymentType?.map((type) => type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()).join(", ") || "Unknown"}
                        </span>
                      </Text>
                      <Text>
                        <span className="font-medium">Deployed By:</span> <span className="text-gray-900 dark:text-white">{deployment.deployedBy}</span>
                      </Text>
                    </View>
                  </View>
                  <Icon
                    name="chevron-right"
                    size={16}
                    className="text-gray-400"
                  />
                </View>
              </View>
            ))}
          </motion.div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <View className="flex flex-row justify-center items-center mt-4 px-2">
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
              variant="ghost"
              className="flex items-center !p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Icon
                name="chevron-left"
                size={16}
                className={currentPage === 1 ? "text-gray-300 dark:text-gray-600" : "text-gray-500 dark:text-gray-400"}
              />
            </Button>

            <View className="flex flex-row items-center gap-1">
              {(() => {
                // Logic to show 5 pages max at a time
                let startPage = Math.max(1, currentPage - 2);
                let endPage = Math.min(totalPages, startPage + 4);

                // Adjust if we're near the end
                if (endPage - startPage < 4) {
                  startPage = Math.max(1, endPage - 4);
                }

                return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
                  <Button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    variant={currentPage === page ? "brand" : "ghost"}
                    className={`!min-w-[32px] !h-8 !p-0 ${
                      currentPage === page ? "!text-white" : "!text-gray-500 dark:!text-gray-400 hover:!bg-gray-100 dark:hover:!bg-gray-800"
                    }`}
                  >
                    <Text className={currentPage === page ? "text-white" : ""}>{page}</Text>
                  </Button>
                ));
              })()}
            </View>

            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === totalPages}
              variant="ghost"
              className="flex items-center !p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <Icon
                name="chevron-right"
                size={16}
                className={currentPage === totalPages ? "text-gray-300 dark:text-gray-600" : "text-gray-500 dark:text-gray-400"}
              />
            </Button>
          </View>
        )}

        {/* Deployment Details Dialog */}
        <DeploymentDetailsDialog
          deployment={agentDeploymentEntities[selectedDeploymentId]}
          availableDeployments={agentDeployments}
          isOpen={!!selectedDeploymentId}
          onClose={onClickCloseDetails}
          getFunctionName={getFunctionName}
          getFunctionInfo={getFunctionInfo}
          getEnvironmentName={getEnvironmentName}
        />
      </motion.div>
    </div>
  );
};
